// ============================================
// Path: /frontend/src/components/NestedSelect.jsx
// Purpose: Custom dropdown with click-based submenu
// this controls how the search in properties works 
// ============================================

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Label } from './ui/label';

export function NestedSelect({ 
  id, 
  label, 
  value, 
  onValueChange, 
  options, 
  placeholder,
  isLocation = false 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHoveredCategory(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getDisplayValue = () => {
    if (!value) return placeholder;
    
    if (value.includes('/')) {
      const [mainType, subType] = value.split('/');
      return `${mainType} - ${subType}`;
    }
    return value;
  };

  const getSubArray = (category) => {
    return isLocation ? category.subCounties : category.subTypes;
  };

  const handleMainCategoryClick = (category) => {
    const subArray = getSubArray(category);
    if (!subArray || subArray.length === 0) {
      onValueChange(category.name);
      setIsOpen(false);
      setHoveredCategory(null);
    } else {
      // Toggle submenu on click for both mobile and desktop
      setHoveredCategory(hoveredCategory === category.name ? null : category.name);
    }
  };

  const handleSubItemClick = (mainType, subItem) => {
    onValueChange(`${mainType}/${subItem}`);
    setIsOpen(false);
    setHoveredCategory(null);
  };

  return (
    <div className="space-y-1.5 sm:space-y-2" ref={dropdownRef}>
      <Label htmlFor={id} className="text-xs sm:text-sm">{label}</Label>
      
      <button
        type="button"
        id={id}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-9 sm:h-10 px-3 py-2 text-xs sm:text-sm text-left bg-background border border-input rounded-md
                   hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                   transition-all duration-200 flex items-center justify-between shadow-sm"
      >
        <span className={!value ? 'text-muted-foreground' : ''}>
          {getDisplayValue()}
        </span>
        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full min-w-[200px] bg-popover border border-border rounded-md shadow-lg overflow-hidden">
          <div className="max-h-80 overflow-y-auto py-1">
            <div
              className="px-3 py-2 text-xs sm:text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground 
                         cursor-pointer transition-colors duration-150"
              onClick={() => {
                onValueChange('');
                setIsOpen(false);
                setHoveredCategory(null);
              }}
            >
              {placeholder}
            </div>

            {options.map((category, index) => {
              const subArray = getSubArray(category);
              const isSelected = value === category.name || value?.startsWith(`${category.name}/`);
              const hasSubmenu = subArray && subArray.length > 0;
              const showSubmenuHere = hasSubmenu && hoveredCategory === category.name;
              
              return (
                <div key={category.name}>
                  <div
                    onClick={() => handleMainCategoryClick(category)}
                    className={`relative px-3 py-2 text-xs sm:text-sm cursor-pointer transition-colors duration-150
                               flex items-center justify-between group
                               ${isSelected 
                                 ? 'bg-accent text-accent-foreground font-medium' 
                                 : 'hover:bg-accent/50 hover:text-accent-foreground'}`}
                  >
                    <span>{category.name}</span>
                    {hasSubmenu && (
                      <ChevronRight className={`w-4 h-4 text-muted-foreground group-hover:text-accent-foreground transition-transform
                                                ${showSubmenuHere ? 'rotate-90' : ''}`} />
                    )}
                  </div>
                  
                  {/* Show submenu inline below the category when expanded */}
                  {showSubmenuHere && (
                    <div className="bg-accent/30 border-l-2 border-accent pl-4">
                      {subArray.map((subItem) => {
                        const isSubSelected = value === `${category.name}/${subItem}`;
                        return (
                          <div
                            key={subItem}
                            onClick={() => handleSubItemClick(category.name, subItem)}
                            className={`px-3 py-2 text-xs sm:text-sm cursor-pointer transition-colors duration-150
                                       ${isSubSelected 
                                         ? 'bg-accent text-accent-foreground font-medium' 
                                         : 'hover:bg-accent/50 hover:text-accent-foreground'}`}
                          >
                            {subItem}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}